import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LayoutChangeEvent, StyleProp, ViewStyle } from "react-native";
import { View, ScrollView, Image } from "@lookiero/aurora";
import { Text } from "@lookiero/sty-psp-ui";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { TouchableWithoutFeedback } from "../touchableWithoutFeedback/TouchableWithoutFeedback";
import { horizontalStyle, verticalStyle, imageStyle as categoryTabsImageStyle } from "./CategoryTabs.style";

interface CategoryTabsProps {
  readonly categoryIds: Array<string>;
  readonly selectedCategoryId: string;
  readonly onPress: (item: string) => void;
  readonly horizontal: boolean;
  readonly i18nKeyExtractor: (categoryId: string) => string;
  readonly style?: StyleProp<ViewStyle>;
}

const CategoryTabs: FC<CategoryTabsProps> = ({
  categoryIds,
  selectedCategoryId,
  onPress,
  horizontal,
  i18nKeyExtractor,
  style: styleFromParent, // is this needed??
}) => {
  const [targetScroll, setTargetScroll] = useState<number>();
  const categoryPressed = useRef(false);
  const tabPositions = useRef<Record<string, number>>({});

  const scrollToCategory = useCallback((categoryId: string) => {
    const position = tabPositions.current[categoryId];

    if (categoryPressed.current || position === undefined) {
      return;
    }

    setTargetScroll(position);
  }, []);

  useEffect(() => scrollToCategory(selectedCategoryId), [scrollToCategory, selectedCategoryId]);

  const handleOnLayout = useCallback(
    (categoryId: string, event: LayoutChangeEvent) => {
      tabPositions.current[categoryId] = event.nativeEvent.layout.x;

      if (categoryId === selectedCategoryId) {
        scrollToCategory(categoryId);
      }
    },
    [scrollToCategory, selectedCategoryId],
  );

  const handleOnPress = useCallback(
    (categoryId: string) => {
      categoryPressed.current = true;
      onPress(categoryId);
    },
    [onPress],
  );

  const {
    cdnPath,
    session: {
      user: { segment },
    },
  } = useStaticInfo();

  const style = horizontal ? horizontalStyle() : verticalStyle();
  const imageStyle = useMemo(() => categoryTabsImageStyle(), []);

  return (
    <ScrollView
      animated={false}
      contentContainerStyle={styleFromParent}
      horizontal={horizontal}
      scrollTo={targetScroll}
      showsHorizontalScrollIndicator={false}
      showsVerticalScrollIndicator={false}
      style={{ flexGrow: 0 }}
      scrollEnabled
    >
      <View style={[style.container, styleFromParent]}>
        {categoryIds
          ? categoryIds.map((categoryId) => {
              const isCurrentSelected = categoryId === selectedCategoryId;

              return (
                <View
                  key={categoryId}
                  testID={`category-${categoryId}`}
                  onLayout={(event) => handleOnLayout(categoryId, event)}
                >
                  <TouchableWithoutFeedback style={style.item} onPress={() => handleOnPress(categoryId)}>
                    {horizontal ? (
                      <View style={[imageStyle.container, isCurrentSelected ? imageStyle.active : undefined]}>
                        <Image
                          src={`${cdnPath}/images/shop/categories/${segment}/${categoryId}.webp`}
                          style={imageStyle.image}
                        />
                      </View>
                    ) : (
                      <></>
                    )}
                    <Text
                      level={horizontal ? 3 : 1}
                      style={[style.text, isCurrentSelected ? style.selectedText : undefined]}
                      variant="detailBold"
                    >
                      {i18nKeyExtractor(categoryId)}
                    </Text>
                  </TouchableWithoutFeedback>
                </View>
              );
            })
          : null}
      </View>
    </ScrollView>
  );
};

export { CategoryTabs };
